import type { ProjectWorkItem } from "@pericles/framework-frontend";

import type { JarvisRunSpecValidationReport } from "../api/jarvisRunSpecClient";

export type JarvisRunSpecLike = {
  workspace?: { packageManager?: string; packageRoot?: string | null; apps?: string[]; packages?: string[] };
  policies?: { allowlistPaths?: string[]; denylistPaths?: string[] };
  redaction?: { assertNoSecrets?: boolean };
  workItem?: { owner?: string | null };
};

export interface DefinitionOfReadyItem {
  id: string;
  label: string;
  satisfied: boolean;
}

export interface DefinitionOfReadyResult {
  ready: boolean;
  items: DefinitionOfReadyItem[];
}

function hasWorkspaceScope(spec: JarvisRunSpecLike): boolean {
  const apps = Array.isArray(spec.workspace?.apps) ? spec.workspace?.apps ?? [] : [];
  const packages = Array.isArray(spec.workspace?.packages) ? spec.workspace?.packages ?? [] : [];
  const packageRoot = spec.workspace?.packageRoot;
  return Boolean(packageRoot && (apps.length > 0 || packages.length > 0));
}

function hasAllowlist(spec: JarvisRunSpecLike): boolean {
  const allowlist = Array.isArray(spec.policies?.allowlistPaths) ? spec.policies?.allowlistPaths ?? [] : [];
  return allowlist.length > 0;
}

function hasSecretsGuard(spec: JarvisRunSpecLike): boolean {
  return spec.redaction?.assertNoSecrets !== false;
}

function hasOwner(workItem: ProjectWorkItem | undefined): boolean {
  return Boolean(workItem?.owner && workItem.owner.trim());
}

export function buildDefinitionOfReady(spec: JarvisRunSpecLike, workItem?: ProjectWorkItem): DefinitionOfReadyResult {
  const items: DefinitionOfReadyItem[] = [
    { id: "workspace", label: "Workspace scoped (package root + app/package set)", satisfied: hasWorkspaceScope(spec) },
    { id: "allowlist", label: "Allowlist paths provided", satisfied: hasAllowlist(spec) },
    { id: "secrets", label: "Secret redaction enforced", satisfied: hasSecretsGuard(spec) },
    { id: "owner", label: "WorkItem owner assigned", satisfied: hasOwner(workItem) },
  ];

  return { ready: items.every((item) => item.satisfied), items };
}

export function describeMissingReadyItems(result: DefinitionOfReadyResult): string[] {
  return result.items.filter((item) => !item.satisfied).map((item) => item.label);
}

export function validationBadgeVariant(report?: JarvisRunSpecValidationReport): "default" | "success" | "danger" {
  if (!report) return "default";
  return report.valid ? "success" : "danger";
}
